import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { obterSala, Sala } from '../../services/salaService';
import { obterCalendarioSala, Reserva } from '../../services/reservaService';
import { format } from 'date-fns';
import SkeletonLoader from '../common/SkeletonLoader';
import { useTheme } from '../../contexts/ThemeContext';
import { corSalaParaTexto } from '../../utils/corSala';

// Função para interpretar data do banco como horário local (sem conversão UTC)
const toBrasiliaTime = (date: Date | string): Date => {
  if (typeof date === 'string') {
    // Remover 'Z' ou timezone da string para forçar interpretação como local
    const dateStr = date.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
    return new Date(dateStr);
  }
  return date;
};

// Funções auxiliares para manipulação de datas
const startOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const endOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

const eachDayOfInterval = (interval: { start: Date; end: Date }): Date[] => {
  const days: Date[] = [];
  const current = new Date(interval.start);
  while (current <= interval.end) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
};

const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear();
};

const addMonths = (date: Date, amount: number): Date => {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + amount);
  return newDate;
};

const subMonths = (date: Date, amount: number): Date => {
  return addMonths(date, -amount);
};

const getMonthName = (date: Date): string => {
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return months[date.getMonth()];
};

const CalendarioReservas: React.FC = () => {
  const { salaId } = useParams<{ salaId: string }>();
  const navigate = useNavigate();
  const { tema } = useTheme();
  const corTexto = (cor?: string) => corSalaParaTexto(cor || '#3498db', tema === 'escuro');

  const [sala, setSala] = useState<Sala | null>(null);
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [mesAtual, setMesAtual] = useState(new Date());
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [reservaSelecionada, setReservaSelecionada] = useState<Reserva | null>(null);
  const [dialogAberto, setDialogAberto] = useState(false);

  useEffect(() => {
    if (salaId) {
      carregarDados();
    }
  }, [salaId, mesAtual]);

  const carregarDados = async () => {
    if (!salaId) return;
    
    try {
      setCarregando(true);
      setErro('');
      
      const [salaData, reservasData] = await Promise.all([
        obterSala(salaId),
        obterCalendarioSala(salaId, mesAtual.getMonth() + 1, mesAtual.getFullYear())
      ]);
      
      setSala(salaData);
      setReservas(reservasData);
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      setErro(error.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setCarregando(false);
    }
  };

  const handleMesAnterior = () => {
    setMesAtual(subMonths(mesAtual, 1));
  };

  const handleProximoMes = () => {
    setMesAtual(addMonths(mesAtual, 1));
  };

  const handleNovaReserva = () => {
    navigate(`/reservas/nova/${salaId}`);
  };

  const handleVerReserva = (reserva: Reserva) => {
    setReservaSelecionada(reserva);
    setDialogAberto(true);
  };

  const handleFecharDialog = () => {
    setDialogAberto(false);
    setReservaSelecionada(null);
  };

  const getReservasDoDia = (dia: Date): Reserva[] => {
    return reservas.filter(reserva => {
      const dataInicio = new Date(reserva.data_inicio);
      const dataFim = new Date(reserva.data_fim);
      return dia >= new Date(dataInicio.toDateString()) && dia <= new Date(dataFim.toDateString());
    });
  };

  const renderCalendario = () => {
    const inicio = startOfMonth(mesAtual);
    const fim = endOfMonth(mesAtual);
    const dias = eachDayOfInterval({ start: inicio, end: fim });
    
    const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    
    const primeiroDiaSemana = inicio.getDay();
    const diasVaziosInicio: null[] = Array(primeiroDiaSemana).fill(null);

    return (
      <Box>
        {/* Cabeçalho dos dias da semana */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
          {diasSemana.map(dia => (
            <Box key={dia} sx={{ textAlign: 'center', py: 1, fontWeight: 'bold', color: 'text.secondary' }}>
              {dia}
            </Box>
          ))}
        </Box>

        {/* Grade do calendário */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
          {/* Dias vazios do início */}
          {diasVaziosInicio.map((_, index) => (
            <Box key={`vazio-${index}`} sx={{ minHeight: 100, bgcolor: 'grey.100', borderRadius: 1 }} />
          ))}
          
          {/* Dias do mês */}
          {dias.map((dia: Date) => {
            const reservasDoDia = getReservasDoDia(dia);
            const isHoje = isSameDay(dia, new Date());
            
            return (
              <Box
                key={dia.toISOString()}
                sx={{
                  minHeight: 100,
                  p: 1,
                  bgcolor: isHoje ? `${sala?.cor}10` : 'background.paper',
                  border: isHoje ? `2px solid ${corTexto(sala?.cor)}` : '1px solid',
                  borderColor: isHoje ? corTexto(sala?.cor) : 'divider',
                  borderRadius: 1,
                  transition: 'box-shadow 0.2s',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isHoje ? 'bold' : 'normal',
                    color: isHoje ? corTexto(sala?.cor) : 'text.primary',
                    mb: 0.5
                  }}
                >
                  {format(dia, 'd')}
                </Typography>

                {reservasDoDia.map((reserva) => (
                  <Chip
                    key={reserva.id}
                    label={`${format(toBrasiliaTime(reserva.data_inicio), 'HH:mm')} - ${format(toBrasiliaTime(reserva.data_fim), 'HH:mm')}`}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleVerReserva(reserva); }}
                    sx={{
                      width: '100%',
                      mb: 0.5,
                      bgcolor: sala?.cor,
                      color: 'white',
                      fontSize: '0.7rem',
                      height: 20,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.85 }
                    }}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  if (carregando) {
    return <SkeletonLoader />;
  }

  if (erro) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{erro}</Alert>
      </Box>
    );
  }

  if (!sala) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Sala não encontrada</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Botão Voltar */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/reservas')}
          sx={{
            color: corTexto(sala.cor),
            borderColor: corTexto(sala.cor),
            '&:hover': {
              borderColor: corTexto(sala.cor),
              bgcolor: `${sala.cor}10`
            }
          }}
        >
          Voltar para Salas
        </Button>
      </Box>

      {/* Cabeçalho */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {sala.nome}
          </Typography>
          <Chip
            label={sala.tipo}
            size="small"
            sx={{
              bgcolor: `${sala.cor}20`,
              color: corTexto(sala.cor),
              fontWeight: 'bold'
            }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNovaReserva}
          sx={{ 
            bgcolor: sala.cor,
            '&:hover': {
              bgcolor: sala.cor,
              filter: 'brightness(0.9)'
            }
          }}
        >
          Nova Reserva
        </Button>
      </Box>

      {/* Navegação do mês */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={handleMesAnterior}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h5">
              {getMonthName(mesAtual)} {mesAtual.getFullYear()}
            </Typography>
            <IconButton onClick={handleProximoMes}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {renderCalendario()}
        </CardContent>
      </Card>

      {/* Legenda */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Legenda
          </Typography>
          <Stack direction="row" spacing={2}>
            <Chip
              label="Hoje"
              size="small"
              sx={{
                bgcolor: `${sala.cor}20`,
                color: corTexto(sala.cor),
                borderColor: corTexto(sala.cor),
                border: '2px solid'
              }}
            />
            <Chip 
              label="Reservado" 
              size="small" 
              sx={{ 
                bgcolor: sala.cor,
                color: 'white'
              }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Dialog de detalhes da reserva */}
      <Dialog open={dialogAberto} onClose={handleFecharDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Detalhes da Reserva
            <IconButton onClick={handleFecharDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {reservaSelecionada && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Título
                </Typography>
                <Typography variant="body1">
                  {reservaSelecionada.titulo}
                </Typography>
              </Box>

              {reservaSelecionada.descricao && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Descrição
                  </Typography>
                  <Typography variant="body1">
                    {reservaSelecionada.descricao}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon fontSize="small" />
                  Horário
                </Typography>
                <Typography variant="body1">
                  {format(toBrasiliaTime(reservaSelecionada.data_inicio), "dd/MM/yyyy 'às' HH:mm")}
                  {' até '}
                  {format(toBrasiliaTime(reservaSelecionada.data_fim), "dd/MM/yyyy 'às' HH:mm")}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon fontSize="small" />
                  Responsável
                </Typography>
                <Typography variant="body1">
                  {reservaSelecionada.usuario_nome}
                </Typography>
              </Box>

              {reservaSelecionada.observacoes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Observações
                  </Typography>
                  <Typography variant="body1">
                    {reservaSelecionada.observacoes}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarioReservas;
